/**
 * Words for what the server says about each row (capture-app
 * services/vendorRecord/importClassify.ts, Decision 4 and rulings 4 to 21),
 * and for the import row's re-stamp state (importApply.ts, ruling 18).
 */
export const HELD_REASONS = [
  'name_taken', 'ambiguous', 'empty_key', 'missing_id', 'missing_name', 'invalid', 'changed_during_import',
];
export const isHeld = (outcome) => HELD_REASONS.includes(outcome);

/** Outcomes in the order the preview lists them. */
export const OUTCOME_ORDER = [
  'create', 'update', 'kept', 'link', 'link_skipped', 'unchanged', 'merged', 'duplicate', ...HELD_REASONS,
];

export const OUTCOME_LABELS = {
  create: 'New vendor',
  update: 'Update',
  unchanged: 'Unchanged',
  kept: 'Your values kept',
  link: 'Link to existing',
  link_skipped: 'Not linked',
  merged: 'Merged',
  duplicate: 'Repeated row',
  name_taken: 'Name belongs to another vendor',
  ambiguous: 'Conflicting rows',
  empty_key: 'Name has no letters or numbers',
  missing_id: 'No id or vendor number',
  missing_name: 'No vendor name',
  invalid: 'Value not accepted',
  changed_during_import: 'Changed during the import',
};

/** What each held reason means and what to do about it. */
export const HELD_HELP = {
  name_taken: 'A printed name on this row already belongs to another vendor. The row is not imported.',
  ambiguous: 'Rows with the same id disagree, or the same name has two ids. None of them is imported.',
  empty_key: 'The vendor name has no letters or numbers to match on.',
  missing_id: 'The row has no external id and no vendor number.',
  missing_name: 'The row has no vendor name.',
  invalid: 'A value on this row is too long or not allowed.',
  changed_during_import: 'Someone changed this vendor while the import ran. Import the file again.',
};

export const FIELD_LABELS = { display_name: 'Display name', vendor_number: 'Vendor number' };

const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** One line per changed field on an update or kept row. */
export function fieldDiffLines(fields) {
  if (!fields) return [];
  const lines = [];
  for (const key of ['display_name', 'vendor_number']) {
    const d = fields[key];
    if (!d || d.action === 'same' || d.action === 'empty') continue;
    const whose = d.current_origin === 'file' ? 'from file' : 'yours';
    const current = d.current === null ? 'empty' : `"${d.current}" (${whose})`;
    if (d.action === 'set') lines.push(`${FIELD_LABELS[key]}: ${current} becomes "${d.file}"`);
    if (d.action === 'kept') lines.push(`${FIELD_LABELS[key]}: ${current} kept; the file says "${d.file}"`);
  }
  return lines;
}

/**
 * The counts that can differ between a preview and its apply. A preview
 * reports every link candidate as link; an apply splits them into link and
 * link_skipped, so the two are compared as one total.
 */
export function countsDiffer(preview, applied) {
  if (!preview || !applied) return false;
  const keys = ['create', 'update', 'unchanged', 'kept', 'merged', 'duplicate', 'held'];
  if (keys.some((k) => preview[k] !== applied[k])) return true;
  return preview.link + preview.link_skipped !== applied.link + applied.link_skipped;
}

export const RESTAMP_UNFINISHED_MESSAGE = 'The vendors were saved, but updating their documents did not finish.';
export const INTERRUPTED_MESSAGE = 'The import was interrupted before it finished. Import the file again: rows already applied show as unchanged.';
/** What follows a document update that did not fully run: a retry when the server allows one (slice 2d). */
export const RETRY_HINT = 'Retry the document update to run it again.';
export const NO_RETRY_HINT = 'Documents pick up the change the next time the vendor is edited.';

/**
 * The page's sentence for an import row from GET /imports/:id or GET
 * /imports: { final, tone: 'ok' | 'warn' | 'error' | 'progress', text,
 * retryable }. retryable is the server's own answer (rulings 35 and 36).
 */
export function restampStatus(imp) {
  const r = (imp && imp.restamp) || {};
  const retryable = Boolean(imp && imp.retryable);
  const hint = retryable ? RETRY_HINT : NO_RETRY_HINT;
  const result = (tone, text) => ({ final: true, tone, text, retryable });
  if (imp && imp.status === 'failed') {
    // Past its stale window in the restamp phase: the vendors were written,
    // the documents may not be, and importing again would restamp nothing.
    if (r.state === 'stale' && r.phase === 'restamp') return result('error', `${RESTAMP_UNFINISHED_MESSAGE} ${hint}`);
    if (r.interrupted || r.state === 'interrupted' || r.state === 'stale') return result('error', INTERRUPTED_MESSAGE);
    return result('error', 'The import failed. Import the file again: rows already applied show as unchanged.');
  }
  if (imp && imp.status === 'applied') {
    if (r.state === 'none') return result('ok', 'No documents needed updating.');
    if (r.state === 'failed') {
      const id = r.error_id ? ` (error id ${r.error_id})` : '';
      return result('error', `The vendors were saved, but updating their documents failed${id}. ${hint}`);
    }
    if (r.state === 'done') {
      const parts = [];
      parts.push(r.stamped > 0 ? `${plural(r.stamped, 'document', 'documents')} updated.` : 'No documents needed updating.');
      if (r.skipped > 0) {
        parts.push(r.skipped === 1
          ? '1 published document keeps its values.'
          : `${r.skipped.toLocaleString('en-US')} published documents keep their values.`);
      }
      if (r.errors > 0) {
        parts.push(`${plural(r.errors, 'document', 'documents')} could not be updated.`);
        if (retryable) parts.push(RETRY_HINT);
      }
      return result(r.errors > 0 ? 'warn' : 'ok', parts.join(' '));
    }
  }
  return { final: false, tone: 'progress', text: 'Updating documents that carry these vendors...', retryable: false };
}

const sentence = (t) => String(t || '').trim().toLowerCase().replace(/\.$/, '');
const GENERIC_NAME_TAKEN = 'a printed name on this row belongs to another vendor';

/**
 * Whether a held row's server reason says more than the help text above it
 * (slice 2d). The server's sentence for missing_name and missing_id is the
 * help text in lower case; name_taken's generic sentence repeats the owner
 * line. A specific reason (which value, which rows) is still shown.
 */
export function serverReasonAddsInfo(outcome, reason) {
  if (!reason) return false;
  const said = sentence(reason);
  if (outcome === 'name_taken' && said === GENERIC_NAME_TAKEN) return false;
  const help = sentence(HELD_HELP[outcome]);
  return !(help && (help === said || help.includes(said)));
}

const SUMMARY_PARTS = [
  ['create', 'new'], ['update', 'updated'], ['link', 'linked'], ['kept', 'kept'], ['unchanged', 'unchanged'], ['held', 'held'],
];

/** One line for an import row's counts: "3 new, 1 updated, 1 held". */
export function importSummaryText(imp) {
  const c = (imp && imp.counts) || {};
  const parts = SUMMARY_PARTS.filter(([k]) => c[k] > 0).map(([k, label]) => `${c[k].toLocaleString('en-US')} ${label}`);
  if (parts.length > 0) return parts.join(', ');
  return typeof imp?.row_count === 'number' ? plural(imp.row_count, 'row', 'rows') : '';
}
